import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ThreeDot } from "react-loading-indicators";
import { usePaths } from "BEDROCK/paths";
import { useStravaOAuth } from "../hooks/use-strava-oauth";
import { useStravaActivities } from "../hooks/use-strava-activities";
import { useAnalyticsTracker } from "analytics-tracker";
import { StravaAnalyticsEvents, StravaOAuthErrorTypes } from "../types";
import styles from "./strava-callback-page.module.css";

type CallbackState =
  | { status: "loading"; message: string }
  | { status: "success" }
  | { status: "error"; message: string; errorCode?: string };

/**
 * Map Edge Function error messages to user-friendly messages.
 */
const getErrorMessage = (error: string): { message: string; errorCode?: string } => {
  const errorLower = error.toLowerCase();

  if (errorLower.includes("no_gps_data") || errorLower.includes("no gps")) {
    return {
      message: "We couldn't find any activities with GPS data. Try uploading a GPX file instead.",
      errorCode: "NO_GPS_DATA",
    };
  }

  if (errorLower.includes("no_activities") || errorLower.includes("no activities")) {
    return {
      message: "No activities found in your Strava account.",
      errorCode: "NO_ACTIVITIES",
    };
  }

  if (errorLower.includes("invalid_code") || errorLower.includes("invalid code") || errorLower.includes("bad request")) {
    return {
      message: "Connection failed. Please try again.",
      errorCode: "INVALID_CODE",
    };
  }

  // Network or unknown errors
  return {
    message: "Connection error. Please check your internet and try again.",
    errorCode: "UNKNOWN",
  };
};

/**
 * OAuth callback page for Strava integration.
 *
 * Handles the OAuth callback by:
 * 1. Extracting code and state from URL params
 * 2. Validating state against localStorage (CSRF protection)
 * 3. Exchanging code for activities list via Edge Function (list mode)
 * 4. Saving activities data and redirecting to activity selection page
 */
export const StravaCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const { navigateTo, stravaLanding, stravaActivities } = usePaths();
  const { validateState, exchangeCodeForActivities, clearOAuthState } = useStravaOAuth();
  const { saveActivitiesData, createActivitiesDataFromResponse, clearActivitiesData } = useStravaActivities();
  const { trackEvent } = useAnalyticsTracker();

  const [callbackState, setCallbackState] = useState<CallbackState>({
    status: "loading",
    message: "Connecting to Strava...",
  });

  // Use ref to prevent running the callback multiple times
  const hasProcessedRef = useRef(false);

  // Run callback flow on mount - only once
  useEffect(() => {
    // Prevent running multiple times
    if (hasProcessedRef.current) {
      return;
    }
    hasProcessedRef.current = true;

    const handleCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");

      console.log("[Strava Callback] Starting callback handler", { code: code?.substring(0, 10) + "...", state, error });

      // Handle Strava OAuth errors (user denied access, etc.)
      if (error) {
        trackEvent(StravaAnalyticsEvents.OAUTH_ERROR, {
          error_type: StravaOAuthErrorTypes.OAUTH_DENIED,
          error,
        });
        clearActivitiesData();
        setCallbackState({
          status: "error",
          message: "Strava connection was cancelled.",
          errorCode: "OAUTH_DENIED",
        });
        return;
      }

      // Validate required params
      if (!code || !state) {
        trackEvent(StravaAnalyticsEvents.OAUTH_ERROR, {
          error_type: StravaOAuthErrorTypes.MISSING_PARAMS,
        });
        clearActivitiesData();
        setCallbackState({
          status: "error",
          message: "Invalid callback. Please try again.",
          errorCode: "MISSING_PARAMS",
        });
        return;
      }

      // Validate state (CSRF protection)
      console.log("[Strava Callback] Validating state...");
      const storedState = validateState(state);
      console.log("[Strava Callback] State validation result:", storedState);
      if (!storedState) {
        trackEvent(StravaAnalyticsEvents.OAUTH_ERROR, {
          error_type: StravaOAuthErrorTypes.INVALID_STATE,
        });
        clearActivitiesData();
        setCallbackState({
          status: "error",
          message: "Security check failed. Please try again.",
          errorCode: "INVALID_STATE",
        });
        return;
      }

      // Exchange code for activities list
      setCallbackState({ status: "loading", message: "Fetching your activities..." });

      try {
        console.log("[Strava Callback] Calling exchangeCodeForActivities...");
        const response = await exchangeCodeForActivities(code);
        console.log("[Strava Callback] Exchange response:", response);

        // Check if we got any activities
        if (!response.activities || response.activities.length === 0) {
          trackEvent(StravaAnalyticsEvents.OAUTH_ERROR, {
            error_type: StravaOAuthErrorTypes.NO_ACTIVITIES,
          });
          clearOAuthState();
          clearActivitiesData();
          setCallbackState({
            status: "error",
            message: "No activities found in your Strava account.",
            errorCode: "NO_ACTIVITIES",
          });
          return;
        }

        // Create and save activities data
        const activitiesData = createActivitiesDataFromResponse(response);
        saveActivitiesData(activitiesData);
        clearOAuthState();

        // Track success
        trackEvent(StravaAnalyticsEvents.OAUTH_SUCCESS, {
          activities_count: response.activities.length,
          athlete_id: response.athlete.id,
          return_to: storedState.returnTo,
        });

        // Show success briefly then redirect
        setCallbackState({ status: "success" });

        // Determine redirect destination based on returnTo path
        // If returnTo is a project/editor path (starts with /project), go back there
        // Otherwise, use the default activities selection flow
        const isDesktopEditorReturn = storedState.returnTo.startsWith("/project");
        const redirectTo = isDesktopEditorReturn ? storedState.returnTo : stravaActivities;

        console.log("[Strava Callback] Redirecting to:", redirectTo, { isDesktopEditorReturn });

        // Auto-redirect after brief success message
        setTimeout(() => {
          navigateTo(redirectTo);
        }, 1000);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error";
        const { message, errorCode } = getErrorMessage(errorMessage);

        trackEvent(StravaAnalyticsEvents.OAUTH_ERROR, {
          error_type: StravaOAuthErrorTypes.EXCHANGE_FAILED,
          error_code: errorCode,
          error_message: errorMessage,
        });

        clearOAuthState();
        clearActivitiesData();
        setCallbackState({
          status: "error",
          message,
          errorCode,
        });
      }
    };

    handleCallback();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    navigateTo(stravaLanding);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {callbackState.status === "loading" && (
          <LoadingState message={callbackState.message} />
        )}

        {callbackState.status === "success" && <SuccessState />}

        {callbackState.status === "error" && (
          <ErrorState message={callbackState.message} onRetry={handleRetry} />
        )}
      </div>
    </div>
  );
};

interface LoadingStateProps {
  message: string;
}

const LoadingState = ({ message }: LoadingStateProps) => (
  <div className={styles.stateContainer}>
    <div className={styles.spinnerContainer}>
      <ThreeDot color="var(--color-primary)" size="medium" text="" textColor="" />
    </div>
    <p className={styles.message}>{message}</p>
  </div>
);

const SuccessState = () => (
  <div className={styles.stateContainer}>
    <div className={styles.successIcon}>✓</div>
    <p className={styles.message}>Activities loaded!</p>
  </div>
);

interface ErrorStateProps {
  message: string;
  onRetry: () => void;
}

const ErrorState = ({ message, onRetry }: ErrorStateProps) => (
  <div className={styles.stateContainer}>
    <div className={styles.errorIcon}>!</div>
    <p className={styles.errorMessage}>{message}</p>
    <button className={styles.retryButton} onClick={onRetry}>
      Try Again
    </button>
  </div>
);
